import { apiClient } from '@applyonline/shared'
import {
  BaseComputeAmountForFrequencyDto,
  ComputedAmountForFrequency,
  ComputeEstimateMonthlyResultDto,
  ComputeFundingDto,
  ComputeFundingResultDto,
  ComputeMonthlyAmountRequestDto,
  MonthlyPayment
} from '@applyonline/shared/types'
import { QueryFunctionContext, useQuery } from '@tanstack/react-query'

export interface ComputeAmountForFrequencyDto extends BaseComputeAmountForFrequencyDto {
  merchantId: string
  paymentPlanId: string | null
  hasCoapplicant: boolean
  include84Months: boolean
}

const AMOUNT_FOR_FREQUENCY = 'amountForFrequency'
const AMOUNT_FOR_PROMOTIONAL_CREDIT_APP = 'amountForPromotionalCreditApp'

const keysFactory = {
  amountForFrequency: (computeParams: ComputeAmountForFrequencyDto | null) =>
    [{ scope: AMOUNT_FOR_FREQUENCY }, computeParams] as const,
  amountForPromotionalCreditApp: (computeFunding: ComputeFundingDto) =>
    [{ scope: AMOUNT_FOR_PROMOTIONAL_CREDIT_APP }, computeFunding] as const,
  estimateMonthlyPayment: (params: ComputeMonthlyAmountRequestDto | null) =>
    [{ scope: 'estimateMonthlyPayment' }, params] as const,
}

const computePaymentAmountForFrequency = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForFrequency']>>) => {
  const response = await apiClient.post('personal/CreditApplication/computeAmountForFrequency', computeParams)
  return response.data as ComputedAmountForFrequency
}

export function useComputePaymentAmountForFrequency(
  computeParams: ComputeAmountForFrequencyDto | null,
): [ComputedAmountForFrequency | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.amountForFrequency(computeParams)],
    queryFn: computePaymentAmountForFrequency,
    enabled: computeParams !== null,
  })

  return [data ?? null, isFetching]
}

const getPromotionalCreditAppPlan = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForPromotionalCreditApp']>>) => {
  const response = await apiClient.post<ComputeFundingResultDto>(
    `personal/CreditApplication/${encodeURIComponent(computeParams.creditApplicationId)}/computeFunding`,
    computeParams,
  )
  return response.data
}

export function useGetPromotionalCreditAppPlan(
  amountRequested: number,
  creditApplicationId: string,
  paymentPlanId: string | null,
): [ComputeFundingResultDto | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.amountForPromotionalCreditApp({
      amountRequested,
      creditApplicationId,
      paymentPlanId,
    } as ComputeFundingDto),
    queryFn: getPromotionalCreditAppPlan,
    enabled: !!creditApplicationId && Boolean(paymentPlanId),
  })

  return [data ?? null, isFetching]
}

const getEstimatedMonthlyPayment = async ({
  queryKey: [_, params],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['estimateMonthlyPayment']>>) => {
  const response = await apiClient.post<ComputeEstimateMonthlyResultDto>(
    'personal/CreditApplication/EstimateMonthlyPayment',
    params,
  )
  return response.data
}

export function useEstimateMonthlyPayment(
  params: ComputeMonthlyAmountRequestDto | null,
): [ComputeEstimateMonthlyResultDto | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.estimateMonthlyPayment(params),
    queryFn: getEstimatedMonthlyPayment,
    enabled: params !== null,
  })

  return [data ?? null, isFetching]
}
export type {
  BaseComputeAmountForFrequencyDto,
  ComputedAmountForFrequency,
  ComputeEstimateMonthlyResultDto,
  ComputeFundingDto,
  ComputeFundingResultDto,
  ComputeMonthlyAmountRequestDto, MonthlyPayment
}
